import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, UserPlus, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import AppHeader from "@/components/AppHeader";
import { Pager, paginate } from "@/components/Pager";
import CustomerDialog from "@/components/CustomerDialog";
import CustomerCard from "@/components/customers/CustomerCard";
import CustomersEmptyState from "@/components/customers/CustomersEmptyState";
import MonthlySummary from "@/components/customers/MonthlySummary";
import FollowupReminders from "@/components/customers/FollowupReminders";
import BackupReminder from "@/components/customers/BackupReminder";
import WorkshopBoard from "@/components/customers/WorkshopBoard";
import { useQuotePrinter } from "@/hooks/useQuotePrinter";
import { docFromStatement } from "@/lib/quoteDoc";
import { statementText } from "@/lib/quoteText";
import { useShopMeta } from "@/hooks/useCurrencySymbol";
import { BACKEND_URL } from "@/lib/backendUrl";

const API = `${BACKEND_URL}/api`;

export default function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [statsKey, setStatsKey] = useState(0);
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, customer: null });
  const shop = useShopMeta();
  const sym = shop.sym;
  const printer = useQuotePrinter();
  const statement = async (c, mode) => {
    const { data } = await axios.get(`${API}/customers/${c.id}/statement`);
    if (!data.invoices.length) return toast.info("No unpaid invoices", { description: `${c.company || c.name} has nothing outstanding` });
    const doc = docFromStatement(data, shop);
    if (mode === "whatsapp") return printer.sharePdf(doc, { text: statementText(doc), phone: c.phone, countryCode: shop.countryCode });
    return printer.exportPdf(doc);
  };

  const load = useCallback(async () => {
    const query = q.trim();
    const { data } = await axios.get(`${API}/customers`, { params: query ? { q: query } : {} });
    setCustomers(data);
    setLoading(false);
  }, [q]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const openDialog = (customer = null) => setDialog({ open: true, customer });
  const [sp, setSp] = useSearchParams();
  const editParam = sp.get("edit");
  useEffect(() => { // /app?edit=<id> (from the fleet page) opens that client's form
    if (!editParam || !customers.length) return;
    const c = customers.find((x) => x.id === editParam);
    if (c) { openDialog(c); setSp({}, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editParam, customers]);

  const save = async (form) => {
    if (dialog.customer) {
      await axios.put(`${API}/customers/${dialog.customer.id}`, form);
      toast.success("Customer updated", { description: "客户已更新" });
    } else {
      await axios.post(`${API}/customers`, form);
      toast.success("Customer added", { description: "客户已添加" });
    }
    load();
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}? Their saved quotes will be kept. Customers with invoices or payments on record cannot be deleted.`)) return;
    try {
      await axios.delete(`${API}/customers/${c.id}`);
      toast.success("Customer deleted");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.message) {
        toast.error("Customer has financial records", { description: d.message, duration: 9000, action: { label: "View SOA", onClick: () => navigate(`/customers/${c.id}/soa`) } });
      } else toast.error("Could not delete customer", { description: typeof d === "string" ? d : undefined });
    }
  };

  const newQuote = (c, v) => navigate(`/quote?customer=${c.id}${v ? `&vehicle=${v.id}` : ""}`);
  const totalVehicles = customers.reduce((n, c) => n + c.vehicles.length, 0);
  const shown = typeFilter === "all" ? customers : customers.filter((c) => (typeFilter === "business") === !!c.is_business);
  const bizCount = customers.filter((c) => c.is_business).length;
  const { pageCount, current, visible } = paginate(shown, page);

  return (
    <div className="min-h-screen workshop-bg text-slate-100">
      <AppHeader right={
        <Button onClick={() => openDialog()} data-testid="add-customer-btn" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold">
          <UserPlus className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">New Customer</span>
        </Button>
      } />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-amber-400 font-semibold">Customer Database · 客户数据库</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-1">Customers <span className="font-zh text-amber-300/70 text-2xl">客户</span></h2>
            <p className="text-sm text-slate-400 mt-1">{customers.length} clients · {totalVehicles} vehicles on file</p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full md:w-auto">
            <div className="relative w-full md:w-80">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="customer-search-input" placeholder="Search name, phone, plate, model… 搜索" className="bg-slate-900/60 border-slate-700 pl-9 h-11" />
            </div>
            <Button variant="outline" onClick={() => navigate("/quote")} data-testid="walkin-quote-btn" className="h-11 border-slate-700 bg-slate-900/60 text-slate-200 hover:bg-slate-800 hover:text-amber-300 shrink-0">
              Walk-in quote <span className="font-zh text-xs ml-1.5 text-amber-300/70">临时报价</span> <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="customer-type-filter">
          {[["all", "All", customers.length], ["business", "Business · B2B", bizCount], ["individual", "Individual", customers.length - bizCount]].map(([k, l, n]) => (
            <button key={k} data-on={typeFilter === k} onClick={() => { setTypeFilter(k); setPage(1); }} data-testid={`customer-filter-${k}`}
              className="px-3 py-1.5 rounded-full text-xs font-semibold border border-slate-700 text-slate-300 transition-colors data-[on=true]:bg-sky-500 data-[on=true]:border-sky-500 data-[on=true]:text-slate-950">{l} <span className="font-mono-num opacity-80">{n}</span></button>
          ))}
        </div>
        <BackupReminder />
        <WorkshopBoard shop={shop} onChange={() => setStatsKey((k) => k + 1)} />
        <MonthlySummary sym={sym} refreshKey={statsKey} />
        <FollowupReminders shop={shop} sym={sym} onChange={() => { setStatsKey((k) => k + 1); load(); }} />

        {loading ? (
          <div className="text-slate-500 py-16 text-center">Loading customers…</div>
        ) : shown.length === 0 ? (
          <CustomersEmptyState searching={!!q} onAdd={() => openDialog()} onWalkIn={() => navigate("/quote")} />
        ) : (
          <>
            <div className="grid gap-3" data-testid="customers-list">
              {visible.map((c) => (
                <CustomerCard key={c.id} c={c} sym={sym}
                  onEdit={() => openDialog(c)}
                  onDelete={() => remove(c)}
                  onQuote={(v) => newQuote(c, v)}
                  onHistory={() => navigate(`/quotes?customer=${c.id}`)}
                  onStatement={c.is_business ? (mode) => statement(c, mode) : undefined}
                  onOpen={() => navigate(`/customers/${c.id}`)} />
              ))}
            </div>
            {pageCount > 1 && <Pager page={current} pageCount={pageCount} total={shown.length} onChange={setPage} testid="customers-pager" />}
          </>
        )}
      </div>

      {printer.portal}
      <CustomerDialog open={dialog.open} onOpenChange={(open) => setDialog((d) => ({ ...d, open }))} customer={dialog.customer} onSave={save} onOpenExisting={(c) => setTimeout(() => openDialog(c), 150)} />
    </div>
  );
}
